import gql from 'graphql-tag';

export const typeDefs = gql`
  extend type CMSCollection {
    """
    Lookup a subset of items from this collection.
    """
    items(
      """
      The locales to filter items by.
      """
      locales: [ID]

      """
      A set of filter conditions for this list. All items returned will match these conditions.
      """
      filter: CMSFilter = {}

      """
      The sort order for this list.
      """
      sort: [CMSSortTerm] = []

      """
      Parameters for shuffling this list of items. If this parameter is provided, then the sort parameter will be
      ignored.
      """
      shuffle: CMSShuffle = {enabled: false}

      """
      The number of items that we'll skip in the beginning of the list.
      """
      skip: Int = 0

      """
      The maximum number of items that we'll return in this list.
      """
      limit: Int = 100
    ): CMSItemConnection
  }

  type CMSItemConnection {
    """
    Number of items in this selection.
    """
    count: Int

    """
    List of items (aka: edges, just to match some naming conventions.)
    """
    edges: [CMSItem]
  }

  """
  Filter terms for a CMS multi-item query.
  """
  input CMSFilter {
    """
    Select only draft-mode items.
    """
    draft: Boolean

    """
    Select only archived items.
    """
    archived: Boolean

    """
    An array of terms that must always be satisfied.
    """
    matchAll: [CMSFilterTerms]

    """
    An array of optional terms. At least ONE of those options need to match.
    """
    matchAny: [CMSFilterAll]
  }

  """
  A single set of terms, used as an option in a matchAny filter.
  """
  input CMSFilterAll {
    """
    Terms for this option.
    """
    matchAll: [CMSFilterTerms]!
  }

  """
  A single "term" object, which allows users to define specific field filters.

  A single "term" can have multiple field filters defined, but due to GraphQL input type restrictions, each one of these
  term objects can only use a filter type once. (ie: You can't filter two fields with PlainTextEq filters, because this
  type only allows that key to be used once.) For that reason, these terms are stored in arrays, and filters located
  within the same array will be merged in the backend.

  Every filter key has two things: a type, and the operation. If the field indicated by the filter isn't the correct
  type, the filter will return an error. The operations are as follows:

  - \`Exists\` - Matches if the field has any data stored within it.
  - \`Eq\` - Matches if the field is precisely equal to the value given.
  - \`Ne\` - Matches if the field is **NOT** precisely equal to the value given.
  - \`In\` - Matches if the field is precisely equal to any of the values given in this array.
  - \`Nin\` - Matches if the field is **NOT** precisely equal to any of the values given in this array.
  - \`Gt\` - Matches if the field is greater than the given value.
  - \`Gte\` - Matches if the field is greater than or equal to the given value.
  - \`Lt\` - Matches if the field is less than the given value.
  - \`Lte\` - Matches if the field is less than or equal to the given value.
  - \`EqAll\` - Matches if the field contains all of the values given. (Used in ItemRefSets.)
  - \`NeAll\` - Matches if the field contains none of the values given. (Used in ItemRefSets.)

  Important: Not all types implement all filter operations.
  """
  input CMSFilterTerms {
    PrimaryKeyExists: CMSFilterPrimaryKeyExists
    PrimaryKeyEq: CMSFilterPrimaryKeySingle
    PrimaryKeyNe: CMSFilterPrimaryKeySingle
    PrimaryKeyIn: CMSFilterPrimaryKeyMulti
    PrimaryKeyNin: CMSFilterPrimaryKeyMulti
    BoolExists: CMSFilterBooleanSingle
    BoolEq: CMSFilterBooleanSingle
    BoolNe: CMSFilterBooleanSingle
    ColorExists: CMSFilterBooleanSingle
    ColorEq: CMSFilterStringSingle
    ColorNe: CMSFilterStringSingle
    ColorIn: CMSFilterStringMulti
    ColorNin: CMSFilterStringMulti
    CommercePriceExists: CMSFilterBooleanSingle
    CommercePriceEq: CMSFilterPriceSingle
    CommercePriceNe: CMSFilterPriceSingle
    CommercePriceGt: CMSFilterPriceSingle
    CommercePriceGte: CMSFilterPriceSingle
    CommercePriceLt: CMSFilterPriceSingle
    CommercePriceLte: CMSFilterPriceSingle
    DateExists: CMSFilterBooleanSingle
    DateEq: CMSFilterDateTimeSingle
    DateNe: CMSFilterDateTimeSingle
    DateGt: CMSFilterDateTimeSingle
    DateGte: CMSFilterDateTimeSingle
    DateLt: CMSFilterDateTimeSingle
    DateLte: CMSFilterDateTimeSingle
    DateIn: CMSFilterDateTimeMulti
    DateNin: CMSFilterDateTimeMulti
    EmailExists: CMSFilterBooleanSingle
    EmailEq: CMSFilterStringSingle
    EmailNe: CMSFilterStringSingle
    EmailIn: CMSFilterStringMulti
    EmailNin: CMSFilterStringMulti
    ImageRefExists: CMSFilterBooleanSingle
    FileRefExists: CMSFilterBooleanSingle
    ExtFileRefExists: CMSFilterBooleanSingle
    ItemRefExists: CMSFilterBooleanSingle
    ItemRefEq: CMSFilterIDSingle
    ItemRefNe: CMSFilterIDSingle
    ItemRefIn: CMSFilterIDMulti
    ItemRefNin: CMSFilterIDMulti
    ItemRefSetExists: CMSFilterBooleanSingle
    ItemRefSetEq: CMSFilterIDSingle
    ItemRefSetNe: CMSFilterIDSingle
    ItemRefSetEqAll: CMSFilterIDMulti
    ItemRefSetNeAll: CMSFilterIDMulti
    ItemRefSetIn: CMSFilterIDMulti
    ItemRefSetNin: CMSFilterIDMulti
    LinkExists: CMSFilterBooleanSingle
    LinkEq: CMSFilterStringSingle
    LinkNe: CMSFilterStringSingle
    LinkIn: CMSFilterStringMulti
    LinkNin: CMSFilterStringMulti
    NumberExists: CMSFilterBooleanSingle
    NumberEq: CMSFilterFloatSingle
    NumberNe: CMSFilterFloatSingle
    NumberGt: CMSFilterFloatSingle
    NumberGte: CMSFilterFloatSingle
    NumberLt: CMSFilterFloatSingle
    NumberLte: CMSFilterFloatSingle
    NumberIn: CMSFilterFloatMulti
    NumberNin: CMSFilterFloatMulti
    EnumOptionExists: CMSFilterBooleanSingle
    EnumOptionEq: CMSFilterStringSingle
    EnumOptionNe: CMSFilterStringSingle
    EnumOptionIn: CMSFilterStringMulti
    EnumOptionNin: CMSFilterStringMulti
    OptionExists: CMSFilterBooleanSingle
    OptionEq: CMSFilterStringSingle
    OptionNe: CMSFilterStringSingle
    OptionIn: CMSFilterStringMulti
    OptionNin: CMSFilterStringMulti
    PhoneExists: CMSFilterBooleanSingle
    PhoneEq: CMSFilterStringSingle
    PhoneNe: CMSFilterStringSingle
    PhoneIn: CMSFilterStringMulti
    PhoneNin: CMSFilterStringMulti
    PlainTextExists: CMSFilterBooleanSingle
    PlainTextEq: CMSFilterStringSingle
    PlainTextNe: CMSFilterStringSingle
    PlainTextGt: CMSFilterStringSingle
    PlainTextGte: CMSFilterStringSingle
    PlainTextLt: CMSFilterStringSingle
    PlainTextLte: CMSFilterStringSingle
    PlainTextIn: CMSFilterStringMulti
    PlainTextNin: CMSFilterStringMulti
    RichTextExists: CMSFilterBooleanSingle
    SetExists: CMSFilterBooleanSingle
    UserExists: CMSFilterBooleanSingle
    UserEq: CMSFilterStringSingle
    UserNe: CMSFilterStringSingle
    UserIn: CMSFilterStringMulti
    UserNin: CMSFilterStringMulti
    VideoExists: CMSFilterBooleanSingle
    VideoEq: CMSFilterStringSingle
    VideoNe: CMSFilterStringSingle
    VideoIn: CMSFilterStringMulti
    VideoNin: CMSFilterStringMulti
    SubscriptionPlanExists: CMSFilterBooleanSingle
  }

  """
  An input Price value.
  """
  input CMSInputPrice {
    """
    The number part of a price. What this value means is determined by the currency, but is similar to how Stripe
    defines its prices.
    """
    value: Int!

    """
    The currency code for this price. Needs to be a 3-character ISO-4217 currency code.
    """
    unit: String!
  }

  """
  Boolean filter for a CMSItem's primary key.
  """
  input CMSFilterPrimaryKeyExists {
    value: Boolean!
  }

  """
  Value filter for a CMSItem's primary key.
  """
  input CMSFilterPrimaryKeySingle {
    value: ID!
  }

  """
  Multi-value filter for a CMSItem's primary key.
  """
  input CMSFilterPrimaryKeyMulti {
    value: [ID]!
  }

  """
  A CMS field identifier.
  """
  input CMSFieldKey {
    """
    The field's ID. (A 32-char hex value. Not a mongo id.)
    """
    id: ID

    """
    The field's slug value.
    """
    slug: String
  }

  """
  Boolean filter for a CMSItem field.
  """
  input CMSFilterBooleanSingle {
    field: CMSFieldKey!
    value: Boolean!
  }

  """
  Single ID filter for a CMSItem field.
  """
  input CMSFilterIDSingle {
    field: CMSFieldKey!
    value: ID!
  }

  """
  Multi ID filter for a CMSItem field.
  """
  input CMSFilterIDMulti {
    field: CMSFieldKey!
    value: [ID]!
  }

  """
  Single DateTime filter for a CMSItem field.
  """
  input CMSFilterDateTimeSingle {
    field: CMSFieldKey!
    value: DateTime!
  }

  """
  Multi DateTime filter for a CMSItem field.
  """
  input CMSFilterDateTimeMulti {
    field: CMSFieldKey!
    value: [DateTime]!
  }

  """
  Single String filter for a CMSItem field.
  """
  input CMSFilterStringSingle {
    field: CMSFieldKey!
    value: String!
  }

  """
  Multi String filter for a CMSItem field.
  """
  input CMSFilterStringMulti {
    field: CMSFieldKey!
    value: [String]!
  }

  """
  Single Float filter for a CMSItem field.
  """
  input CMSFilterFloatSingle {
    field: CMSFieldKey!
    value: Float!
  }

  """
  Multi Float filter for a CMSItem field.
  """
  input CMSFilterFloatMulti {
    field: CMSFieldKey!
    value: [Float]!
  }

  """
  Single Price filter for a CMSItem field.
  """
  input CMSFilterPriceSingle {
    field: CMSFieldKey!
    value: CMSInputPrice!
  }

  """
  A term in a sort specifier.
  """
  input CMSSortTerm {
    """
    The field being sorted.
    """
    field: CMSFieldKey!
    """
    The direction of sorting.
    """
    dir: CMSSortDirection!
  }

  """
  The direction of a sort.
  """
  enum CMSSortDirection {
    """
    An _Ascending_ sort.
    """
    ASC

    """
    An _Descending_ sort.
    """
    DESC
  }

  """
  Information on a shuffled CMS ordering.
  """
  input CMSShuffle {
    """
    Whether the shuffle is enabled.
    """
    enabled: Boolean!

    """
    An optional string that is ignored by the backend. This value is used by frontend to differentiate between shuffled
    entries within the client cache.
    """
    key: String

    """
    The shuffle seed used when randomizing list order. Assuming the collection itself hasn't changed, providing the same
    shuffle seed to the backend will result in a CMS list in the same order. (So, for example, pagination would work as
    expected.)
    """
    seed: String
  }
`;
